import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import cn from 'classnames';
import { IconDrag } from '@direct-frontend/components';
import { useDraggableRowStyle } from '../../lib/hooks/useDraggableRowStyle';
import { isTreeRow } from '../../lib/isTreeRow';
import { type DroppableOverData } from '../../lib/types';
import { DroppableSlot } from '../DroppableSlot';
import { type DraggableRowProps } from './types';
import styles from './DraggableRow.module.css';

export const DraggableRow = <Attributes = unknown,>({
    'data-testid': dataTestId = 'DraggableRow',
    node,
    index,
    size,
    renderNode,
    IconDnd = IconDrag,
    iconSize = '20',
    settings,
}: DraggableRowProps<Attributes>) => {
    const context = useDndContext();
    const { active, over } = context;

    const activeNode = active?.data.current;

    const isDraggingOther = isTreeRow(activeNode) && activeNode.id !== node.id;
    const isCurrentRowMovableWithActive = isDraggingOther && activeNode.childrenIds.has(node.id);
    const isPreviousRowIsActive =
        isTreeRow(activeNode) && activeNode.index + activeNode.childrenIds.size === node.index - 1;

    const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
        id: node.id,
        disabled: isDraggingOther || isCurrentRowMovableWithActive,
        data: node,
    });

    const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
        id: node.id,
        disabled: isDragging || isCurrentRowMovableWithActive,
        data: {
            type: 'node',
            node,
        } satisfies DroppableOverData,
    });

    const style = useDraggableRowStyle(node, active, over, { size });

    const overData = over?.data.current;

    if (isCurrentRowMovableWithActive) {
        return null;
    }

    return (
        <>
            <DroppableSlot
                size={size}
                isDroppable={!isCurrentRowMovableWithActive && !isPreviousRowIsActive}
                node={node}
            />
            <div
                data-testid={`${dataTestId}`}
                ref={setNodeRef}
                className={cn(
                    styles.container,
                    {
                        [styles.firstLevel]: !settings?.noBorder && index !== 0 && node.level === 0,
                        [styles.freeze]: isDraggingOther,
                        [styles.dragging]: isDragging,
                        [styles.ghost]: node.id !== overData?.node?.id,
                        [styles.parent]: overData?.node?.parentId === node.id,
                        [styles.over]: isOver,
                    },
                    settings?.itemStyles
                )}
                style={style}
            >
                <div ref={setDroppableNodeRef}>
                    {renderNode({
                        size,
                        node,
                        beforeAddon: (
                            <button
                                {...listeners}
                                {...attributes}
                                className={cn(styles.dragButton, {
                                    [styles.sizeM]: size === 'm',
                                    [styles.sizeS]: size === 's',
                                })}
                                data-testid={`${dataTestId}.DragButton`}
                            >
                                {settings?.dragIcon || (
                                    <IconDnd
                                        size={iconSize}
                                        role="supplementary"
                                        color={isDraggingOther ? 'gray-disabled' : 'gray'}
                                    />
                                )}
                            </button>
                        ),
                        'data-testid': `${dataTestId}.Item`,
                        isSelected: false,
                        isOpen: true,
                        isOpenDisabled: true,
                        dragListeners: listeners,
                    })}
                </div>
            </div>
            <DroppableSlot size={size} isDroppable={false} node={node} isHighlightByParent />
        </>
    );
};
